<template>
  <adv-button-view-buy-picker
    :show-quick-view="false"
    :product-id="productId"
    :product-code-id="productCodeId"
    :is-listing="false"
    :is-variation-group-master="false"
    :unavailable-product="unavailableProduct"
    :recommendation-type="recommendationType"
    :recommendation-id="recommendationId"
  >
  </adv-button-view-buy-picker>
</template>

<script>
import {cloneDeep, get, isEmpty, some} from 'lodash-es'
import store from '../../vue/store'
import productMixin from './mixins/productMixins'
import AdvCartButton from './AdvCartButton'
import AdvProductInputSpinner from './AdvProductInputSpinner'
import {mapActions, mapGetters} from 'vuex'
import AdvButtonViewBuyPicker from "./AdvButtonViewBuyPicker.vue";

export default {
  name: 'AdvProductAddCartMobile',
  components: {
    AdvCartButton,
    AdvProductInputSpinner,
    AdvButtonViewBuyPicker
  },
  props: {
    productId: {
      type: Number
    },
    showName: {
      type: Boolean,
      default: false
    },
    showContent: {
      type: Boolean,
      default: false
    },
    modal: {
      type: Boolean,
      default: false
    },
    recommendationType: {
      type: String,
      default: null
    },
    recommendationId: {
      type: String,
      default: null
    }
  },
  mixins: [productMixin],
  data() {
    return {
      version: '0.0.2',
      productCodeId: null,
      quantity: 1,
      showCard: true,
      progress: false,
    }
  },
  mounted() {
    this.setProductCodeIdForOneOnOne()
  },
  watch: {
    productId() {
      this.setProductCodeIdForOneOnOne()
    }
  },
  computed: {
    ...mapGetters([
      'getUniqueProductVariationValueIds',
      'cartProductWithCustomizationQuantity',
      'cartProductCodeQuantity',
      'getSelectedCurrency'
    ]),
    ...mapGetters('cartProductCustomizations', ['getSchemaById']),
    ...mapGetters('productChart', ['getProductChartSettings']),
    rootCss() {
      if (this.modal) {
        return 'modalQuickV'
      }
      return ''
    },
    config() {
      return store.getters.config
    },
    hasAttributes() {
      return some(this.productData.productCodes, (e) => {
        return e.productCodeAttributes.length > 0
      })
    },
    getCustomizationSchema() {
      if (!('customizationSchemaId' in this.productData) || null === this.productData.customizationSchemaId) {
        return null
      }
      if (('customizationSchemaRange' in this.productData) && this.productData.customizationSchemaRange !== null) {
        const from = this.productData.customizationSchemaRange.from
        const to = this.productData.customizationSchemaRange.to
        if (from !== null && new Date(from) > new Date()) {
          return null
        }
        if (to !== null && new Date(to) < new Date()) {
          return null
        }
      }
      const schema = this.getSchemaById(this.productData.customizationSchemaId)
      if (null === schema || !('schema' in schema) || ('is_active' in schema && false === schema.is_active)) {
        return null
      }
      return cloneDeep(schema)
    },
    hasVariationEntries() {
      return this.getUniqueProductVariationValueIds.length > 0
    },
    productPriceClass() {
      return 'col-xs-12 ' + (this.productData.productFinalPrice === this.productData.productOldPrice ? '' : 'col-sm-4 ') + 'text-right productItemMainPrice'
    },
    inactiveProduct() {
      return !this.productCodeData.productActive
    },
    unavailableProduct() {
      return this.productCodeData.productStock <= 0 || this.productData.productFinalPrice <= 0 || this.isProductInAvailabilityDate
    },
    isProductInAvailabilityDate() {
      const {availableFrom, availableUntil} = this.productData
      const currentDate = Date.now()
      return (
        (availableFrom != null && currentDate < new Date(availableFrom).getTime()) ||
        (availableUntil != null && currentDate > new Date(availableUntil).getTime())
      )
    },
    cartLimitForProductReached() {
      return this.productCodeData.productStock === this.cartQuantity
    },
    selectedAttributeId() {
      return parseFloat(store.getters.getSelectedAttributeId)
    },
    cartQuantity() {
      if (
        null !== this.getCustomizationSchema &&
        !isEmpty(get(this, ['$refs', 'customizationEditor', 'getSchema']))
      ) {
        return this.cartProductWithCustomizationQuantity(
          this.productCodeId,
          this.$refs.customizationEditor.getSchema,
          0
        )
      }
      return this.cartProductCodeQuantity(this.productCodeId, 0)
    },
    maxAmountForInputSpinner() {
      return this.productCodeData.productStock - this.cartQuantity
    },
    variationsActive() {
      return store.getters.getVariationsActive
    },
    productChartSettings() {
      return this.getProductChartSettings
    },
    hasDiscount() {
      return this.productChartSettings?.enabled &&
        this.productData.priceTracking?.shouldDisplay &&
        this.productData.priceTracking?.discountPass
    },
    hasDiscountNoPriceTracking() {
      return !this.productChartSettings?.enabled && this.productData.productHasDiscount
    }
  },
  methods: {
    ...mapActions(['addToCartClicked', 'setSelectedProductCodeId', 'setSelectedAttributeId']),
    setProductCodeIdForOneOnOne() {
      if (!this.hasAttributes) {
        this.codeIdChanged(this.productData.productCodes[0].productCodeId)
        return
      }

      this.quantity = 1
    },
    closeModal() {
      this.showCard = false
      this.$emit('close-quick-view')
      this.clearSelectedAttributeId()
    },
    clearSelectedAttributeId() {
      if (this.hasAttributes) {
        this.setSelectedAttributeId(null)
      }
    },
    priceWithSign(priceString, priceSign) {
      return (parseFloat(priceString) * this.getSelectedCurrency.rate).toFixed(2) + priceSign
    },
    getSign() {
      return this.getSelectedCurrency.symbol
    },
    diffStringPrices(priceA, priceB) {
      return (parseFloat(priceA) - parseFloat(priceB)).toFixed(2).toString()
    },
    attributeSelected(attributeId) {
      this.setSelectedAttributeId(attributeId)
    },
    codeIdChanged(productCodeId) {
      this.productCodeId = productCodeId
      if (this.productCodeId) {
        this.setSelectedProductCodeId(this.productCodeId)
      }
    },
    quantityChanged(quantity) {
      this.quantity = quantity
    },
    addToCard() {
      if ('customizationEditor' in this.$refs && !this.$refs.customizationEditor.validate()) {
        return
      }
      this.progress = true
      const cartPayload = {
        productCodeId: this.productCodeId,
        quantity: this.cartQuantity + this.quantity,
        recommendationId: this.recommendationId,
        recommendationType: this.recommendationType
      }
      if (this.getCustomizationSchema !== null) {
        cartPayload.quantity = this.quantity
        cartPayload.cartProductCustomizations = this.$refs.customizationEditor.getSchema
      }
      this.addToCartClicked(cartPayload).then(() => {
        this.progress = false
        this.quantity = 1
      })
    },
  }
}
</script>
